/**
 * Highway divider (external)
 *
 * Draws the highway divider lines and road surface as an independent DOM
 * subtree appended to document.body - not a child of #gameBoard, no shared
 * CSS/JS with the Game class. Mirrors #gameWrapper's on-screen geometry
 * (measured, not imported) so it lines up pixel-for-pixel despite living
 * outside #gameBoard.
 *
 * #gameBoard's background is transparent (gameBoard.css) so this module's
 * road shows through except where #driverCar/trucks are drawn - that's the
 * one place disabling this import visibly breaks something.
 *
 * Reads speed from debugOverlay.getSpeedState(). The only other coupling is
 * the host's static styles (position/z-index/etc), left in gameBoard.css's
 * .exterHighwayDivider so they're easy to tune in one place.
 *
 * Opt-in: importing attaches it; comment the import to disable.
 */

import { BOARD_HEIGHT, DIVIDER_HEIGHT, FRAME_RATE } from "../../gearshiftGame/constants";
import { getDebugOverlay } from "./debugOverlay";

const HOST_ID = "externalHighwayDividerHost";
const POLL_INTERVAL_MS = 250;

// Mirrors #gameBoard's dimensions (gameBoard.css), duplicated so this module has no
// stylesheet dependency.
const BOARD_WIDTH = 450;
const BOARD_TOP_OFFSET = 150;

// Same lane-divider x-offsets as the in-game highway (game.js#drawDividers)
const DIVIDER_LEFTS = [60, 125, 190, 255, 320, 385];

// The row pattern depends only on pointerTop % this - wrapping at BOARD_HEIGHT (550, not
// a multiple of 40) would shift the pattern 30px every cycle.
const DIVIDER_SPACING = DIVIDER_HEIGHT * 2;

class HighwayDivider {
  constructor() {
    this.host = null;
    this.board = null;
    this.track = null;
    this.pointerTop = 0;
    this.rafId = null;
    this.timeoutId = null;
    this.pollIntervalId = null;
    this.handleResize = () => this.syncPosition();
  }

  show() {
    if (this.pollIntervalId) return; // Already running

    this.sync();
    this.pollIntervalId = setInterval(() => this.sync(), POLL_INTERVAL_MS);
    window.addEventListener("resize", this.handleResize);
  }

  hide() {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
    window.removeEventListener("resize", this.handleResize);
    this.detach();
  }

  /** Tracks #gameWrapper's presence/position only - not synced to trial/phase state */
  sync() {
    const gameWrapper = document.getElementById("gameWrapper");

    if (!gameWrapper) {
      this.detach();
      return;
    }

    if (!this.host) this.attach();
    this.syncPosition(gameWrapper);
  }

  /** Builds the independent host + its own perspective/rotateX chain (mirrors #gameWrapper > #gameBoard) */
  attach() {
    const host = document.createElement("div");
    host.id = HOST_ID;
    // Static styles live in gameBoard.css (.exterHighwayDivider); only the per-sync
    // top/left/width/height/transform stay inline, since those are computed dynamically.
    host.classList.add("exterHighwayDivider");

    const board = document.createElement("div");
    Object.assign(board.style, {
      position: "relative",
      width: `${BOARD_WIDTH}px`,
      height: `${BOARD_HEIGHT}px`,
      margin: "auto",
      top: `${BOARD_TOP_OFFSET}px`,
      transformStyle: "preserve-3d",
      transform: "rotateX(90deg)",
    });

    // Stands in for #gameBoard's own (now transparent) background. No transform, so the
    // divider lines (translateZ(222px) in drawDividerRow()) sort in front of it automatically.
    const boardBackground = document.createElement("div");
    Object.assign(boardBackground.style, {
      position: "absolute",
      inset: "0",
      background: "#545454",
    });
    board.appendChild(boardBackground);

    const track = document.createElement("div");
    board.appendChild(track);
    host.appendChild(board);
    document.body.appendChild(host);

    this.host = host;
    this.board = board;
    this.track = track;

    this.pointerTop = 0;
    this.tick();
  }

  detach() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.host && this.host.parentNode) {
      this.host.parentNode.removeChild(this.host);
    }
    this.host = null;
    this.board = null;
    this.track = null;
  }

  /** Keeps the host tracking #gameWrapper's real geometry (resize, responsive media queries, re-mounts) */
  syncPosition(gameWrapper) {
    if (!this.host) return;

    const wrapper = gameWrapper || document.getElementById("gameWrapper");
    if (!wrapper) return;

    // offsetWidth/Height ignore transforms (unlike getBoundingClientRect) - perspective's math
    // needs #gameWrapper's natural size, not an already-scaled one.
    const naturalWidth = wrapper.offsetWidth;
    const naturalHeight = wrapper.offsetHeight;

    // getBoundingClientRect: final on-screen box, used to find position + scale factor.
    const rect = wrapper.getBoundingClientRect();
    const scale = naturalHeight > 0 ? rect.height / naturalHeight : 1;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Natural size, centered on the real center, then scaled - mirrors how #gameWrapper
    // establishes its own perspective before being scaled down.
    Object.assign(this.host.style, {
      left: `${centerX - naturalWidth / 2}px`,
      top: `${centerY - naturalHeight / 2}px`,
      width: `${naturalWidth}px`,
      height: `${naturalHeight}px`,
      transform: `scale(${scale})`,
    });
  }

  /** Draws one row of dividers (mirrors game.js#drawDividers) */
  drawDividerRow(top) {
    const height = Math.min(DIVIDER_HEIGHT, BOARD_HEIGHT - top);
    return DIVIDER_LEFTS.map((left) => {
      const divider = document.createElement("div");
      // Inline (not .highwayDivider) - no dependency on gameBoard.css
      Object.assign(divider.style, {
        position: "absolute",
        width: "5px",
        background: "white",
        zIndex: "999",
        transform: "translateZ(222px)", // Same z-fighting fix as .highwayDivider
        top: `${top}px`,
        left: `${left}px`,
        height: `${height}px`,
      });
      return divider;
    });
  }

  /** Regenerates every divider row around the current pointer (mirrors game.js#generateHighWayDividers) */
  render() {
    const allDividers = [];

    let beforePointer = this.pointerTop - DIVIDER_SPACING;
    while (beforePointer >= 0) {
      allDividers.push(...this.drawDividerRow(beforePointer));
      beforePointer -= DIVIDER_SPACING;
    }

    let afterPointer = this.pointerTop;
    while (afterPointer < BOARD_HEIGHT) {
      allDividers.push(...this.drawDividerRow(afterPointer));
      afterPointer += DIVIDER_SPACING;
    }

    while (this.track.firstChild) this.track.removeChild(this.track.firstChild);
    allDividers.forEach((divider) => this.track.appendChild(divider));
  }

  /** Reads the speed currently set by the player, straight from the debug overlay */
  getCurrentDriverSpeed() {
    const speed = getDebugOverlay().getSpeedState();
    return typeof speed === "number" ? speed : 0;
  }

  /** Continually called to scroll the highway at the player's current speed (mirrors game.js#moveHighwayDividers) */
  tick() {
    if (!this.track) return;

    this.render();

    let speed = this.getCurrentDriverSpeed();
    if (speed === 0) speed = 1; // Keep the road moving even when the player is stopped, same as the in-game highway

    this.pointerTop += speed;
    // Wrap at DIVIDER_SPACING, not BOARD_HEIGHT (550 isn't a multiple of 40 - that would
    // shift the pattern 30px every cycle, a visible jump).
    if (this.pointerTop >= DIVIDER_SPACING) this.pointerTop -= DIVIDER_SPACING;

    // Matches game.js#render's pacing - a bare requestAnimationFrame would run ~2x too fast.
    this.timeoutId = setTimeout(() => {
      this.rafId = requestAnimationFrame(() => this.tick());
    }, 1000 / FRAME_RATE);
  }
}

let globalHighwayDivider = null;

/**
 * Gets or creates the global highway divider instance.
 * @returns {HighwayDivider}
 */
export function getHighwayDivider() {
  if (!globalHighwayDivider) globalHighwayDivider = new HighwayDivider();
  return globalHighwayDivider;
}

export function showHighwayDivider() {
  getHighwayDivider().show();
}

export function hideHighwayDivider() {
  if (globalHighwayDivider) globalHighwayDivider.hide();
}

// Importing this module enables it immediately - that's the intended
// "wire it in or don't" switch, no extra setup call needed.
if (typeof document !== "undefined") {
  if (document.body) {
    showHighwayDivider();
  } else {
    document.addEventListener("DOMContentLoaded", showHighwayDivider);
  }
}
